export const validationRules = {
  email: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
  phone: /^\d{10}$/,
  aadhar: /^\d{12}$/,
  pan: /^[A-Z]{5}[0-9]{4}[A-Z]{1}$/,
  ifsc: /^[A-Z]{4}[0-9]{7}$/,
  pincode: /^\d{6}$/
};

export const validatePersonalInformation = (data) => {
  const errors = {};

  // Basic Information Validation
  if (!data.firstName?.trim()) errors.firstName = "First name is required";
  if (!data.lastName?.trim()) errors.lastName = "Last name is required";
  if (!data.fatherName?.trim()) errors.fatherName = "Father name is required";
  if (!data.motherName?.trim()) errors.motherName = "Mother name is required";

  // Phone Validation
  if (!data.alternativePhone?.trim()) {
    errors.alternativePhone = "Alternative phone number is required";
  } else if (!validationRules.phone.test(data.alternativePhone)) {
    errors.alternativePhone = "Please enter a valid 10-digit phone number";
  }

  // Required Fields
  if (!data.gender) errors.gender = "Gender is required";
  if (!data.dob) {
    errors.dob = "Date of birth is required";
  } else {
    const birthDate = new Date(data.dob);
    const today = new Date();
    let age = today.getFullYear() - birthDate.getFullYear();
    const m = today.getMonth() - birthDate.getMonth();
    if (m < 0 || (m === 0 && today.getDate() < birthDate.getDate())) {
      age--;
    }
    if (age < 18) errors.dob = "Employee must be at least 18 years old";
  }
  if (!data.nationality) errors.nationality = "Nationality is required";
  if (!data.religion) errors.religion = "Religion is required";
  if (!data.bloodGroup) errors.bloodGroup = "Blood group is required";
  if (!data.maritalStatus) errors.maritalStatus = "Marital status is required";

  // Spouse Name Validation (if married)
  if (data.maritalStatus === 'Married' && !data.spouseName?.trim()) {
    errors.spouseName = "Spouse name is required when married";
  }

  if (data.physicallyChallenged === 'Yes') {
    if (!data.disabilityDetails?.trim()) {
      errors.disabilityDetails = "Disability details are required";
    }
    if (!data.disabilityCertificate || data.disabilityCertificate.length === 0) {
      errors.disabilityCertificate = "Disability certificate is required";
    }
  }

  // ID Validation
  if (!data.aadharNo?.trim()) {
    errors.aadharNo = "Aadhar number is required";
  } else if (!validationRules.aadhar.test(data.aadharNo)) {
    errors.aadharNo = "Aadhar number must be exactly 12 digits";
  }

  if (!data.aadharFrontDocument || data.aadharFrontDocument.length === 0) {
    errors.aadharFrontDocument = "Please upload Aadhaar front side";
  }
  if (!data.aadharBackDocument || data.aadharBackDocument.length === 0) {
    errors.aadharBackDocument = "Please upload Aadhaar back side";
  }

  if (!data.panNo?.trim()) {
    errors.panNo = "PAN number is required";
  } else if (!validationRules.pan.test(data.panNo)) {
    errors.panNo = "Please enter a valid PAN number (e.g. ABCDE1234F)";
  }

  if (!data.panDocument || data.panDocument.length === 0) {
    errors.panDocument = "Please upload PAN document";
  }

  // Emergency Contact Validation
  if (!data.emergencyContact?.relation) {
    errors.emergencyRelation = "Emergency contact relation is required";
  }
  if (!data.emergencyContact?.name?.trim()) {
    errors.emergencyName = "Emergency contact name is required";
  }
  if (!data.emergencyContact?.phone?.trim()) {
    errors.emergencyPhone = "Emergency contact phone is required";
  } else if (!validationRules.phone.test(data.emergencyContact.phone)) {
    errors.emergencyPhone = "Emergency contact phone must be 10 digits";
  }

  // Address Validation
  const presentAddr = data.presentAddress || {};
  if (!presentAddr.accommodationType) {
    errors.accommodationType = "Accommodation type is required";
  }

  const requiredAddrFields = ['houseNo', 'street', 'landmark', 'city', 'state', 'pincode'];
  requiredAddrFields.forEach(field => {
    if (!presentAddr[field]) {
      errors[`present_${field}`] = `Present address ${field.replace(/([A-Z])/g, ' $1').toLowerCase()} is required`;
    }
  });

  const permanentAddr = data.permanentAddress || {};
  requiredAddrFields.forEach(field => {
    if (!permanentAddr[field]) {
      errors[`permanent_${field}`] = `Permanent address ${field.replace(/([A-Z])/g, ' $1').toLowerCase()} is required`;
    }
  });

  return errors;
};

export const validateBankDetails = (data) => {
  const errors = {};

  if (!data.accountName?.trim()) errors.accountName = "Account holder name is required";
  if (!data.accountNumber?.trim()) errors.accountNumber = "Account number is required";
  if (!data.bankName?.trim()) errors.bankName = "Bank name is required";
  if (!data.branchName?.trim()) errors.branchName = "Branch name is required";
  if (!data.ifscCode?.trim()) {
    errors.ifscCode = "IFSC code is required";
  }

  if (!data.passbookDocument || data.passbookDocument.length === 0) {
    errors.passbookDocument = "Cancellation check or bank passbook is required";
  }

  return errors;
};

export const validateEducationDetails = (educationList, educationLevel) => {
  const errors = {};
  const filledEducation = educationList.filter(edu =>
    edu.qualificationLevel && edu.degree && (edu.cgpaPercentage || edu.cgpa)
  );

  // Check minimum required education entries
  if (educationLevel === 'level1' && filledEducation.length < 1) {
    errors._form = "Please add at least 1 education detail (e.g. 10th)";
  } else if (['level2', 'level3', 'level4'].includes(educationLevel) && filledEducation.length < 2) {
    errors._form = "Please add at least 2 education details (e.g. 10th and 12th)";
  }

  // Validate each education entry
  educationList.forEach((edu, idx) => {
    // If the user hasn't entered anything and there's only 1 empty block, we might skip
    // But since education is required, we should validate it if it's the required one or if they've partially filled it

    // Always validate if it's the first one OR if any field is filled
    const isFirst = idx === 0;
    const isPartiallyFilled = edu.qualificationLevel || edu.degree || edu.collegeName || edu.affiliatedUniversity || edu.state || edu.yearOfJoining || edu.yearOfCompletion || edu.cgpaPercentage || (edu.attachments && edu.attachments.length > 0);

    if (isFirst || isPartiallyFilled) {
      if (!edu.qualificationLevel) {
        errors[`qualification_${idx}`] = "Qualification level is required";
      }
      if (!edu.degree?.trim()) {
        errors[`degree_${idx}`] = "Degree/certificate name is required";
      }
      if (!edu.collegeName?.trim()) {
        errors[`college_${idx}`] = "College/school name is required";
      }
      if (!edu.affiliatedUniversity?.trim()) {
        errors[`affiliatedUniversity_${idx}`] = "University/Board is required";
      }
      if (!edu.state?.trim()) {
        errors[`state_${idx}`] = "State is required";
      }
      if (!edu.yearOfJoining || (edu.yearOfJoining instanceof Date && isNaN(edu.yearOfJoining.getTime()))) {
        errors[`joining_${idx}`] = "Year of joining is required";
      }
      if (!edu.yearOfCompletion || (edu.yearOfCompletion instanceof Date && isNaN(edu.yearOfCompletion.getTime()))) {
        errors[`completion_${idx}`] = "Year of completion is required";
      }

      // Validate that completion year is after joining year
      if (edu.yearOfJoining && edu.yearOfCompletion) {
        const joinYear = edu.yearOfJoining instanceof Date ? edu.yearOfJoining.getFullYear() : parseInt(edu.yearOfJoining);
        const completeYear = edu.yearOfCompletion instanceof Date ? edu.yearOfCompletion.getFullYear() : parseInt(edu.yearOfCompletion);

        if (!isNaN(joinYear) && !isNaN(completeYear)) {
          if (completeYear < joinYear) {
            errors[`completion_${idx}`] = "Year of completion cannot be before year of joining";
          }
        }
      }
      if (!edu.cgpaPercentage && edu.cgpaPercentage !== 0) {
        errors[`cgpa_${idx}`] = "CGPA/percentage is required";
      } else if (isNaN(parseFloat(edu.cgpaPercentage))) {
        errors[`cgpa_${idx}`] = "Please enter a valid number";
      } else if (parseFloat(edu.cgpaPercentage) < 0) {
        errors[`cgpa_${idx}`] = "Value cannot be negative";
      } else if (parseFloat(edu.cgpaPercentage) > 100 && !edu.cgpaPercentage.includes('.')) {
        errors[`cgpa_${idx}`] = "Percentage cannot exceed 100";
      } else if (parseFloat(edu.cgpaPercentage) > 10 && edu.cgpaPercentage.includes('.')) {
        errors[`cgpa_${idx}`] = "CGPA cannot exceed 10";
      }
      if (!edu.attachments || edu.attachments.length === 0) {
        errors[`attachments_${idx}`] = "Please upload education documents";
      }
    }
  });

  return errors;
};

export const validateEmploymentDetails = (employmentList, isFresher = false) => {
  const errors = {};

  employmentList.forEach((emp, idx) => {
    // Every entry in the employment list is mandatory (user explicitly added it)
    if (!emp.companyName?.trim()) {
      errors[`company_${idx}`] = "Company name is required";
    }
      if (!emp.role?.trim()) {
        errors[`role_${idx}`] = "Role/position is required";
      }
      if (!emp.ctc?.trim()) {
        errors[`ctc_${idx}`] = "CTC is required";
      } else if (isNaN(parseFloat(emp.ctc)) || parseFloat(emp.ctc) <= 0) {
        errors[`ctc_${idx}`] = "Please enter a valid CTC amount";
      }
      if (!emp.fromDate) {
        errors[`fromDate_${idx}`] = "From date is required";
      }
      if (!emp.toDate) {
        errors[`toDate_${idx}`] = "To date is required";
      }

      if (emp.fromDate && emp.toDate) {
        const startDate = new Date(emp.fromDate);
        const endDate = new Date(emp.toDate);
        if (endDate <= startDate) {
          errors[`dateRange_${idx}`] = "To date must be later than from date";
        }
      }

      // Individual document validation - mandatory for non-freshers on their last employment
      if (!isFresher && emp.isLastEmployment) {
        if (!emp.payslips || emp.payslips.length === 0) {
          errors[`payslips_${idx}`] = "Last 3 months salary slips are required";
        }
      }
  });

  if (!isFresher && employmentList.length > 0) {
    const hasLastEmployment = employmentList.some(emp => emp.isLastEmployment);
    if (!hasLastEmployment) {
      errors['global_employment'] = "Please mark at least one employment as your primary/recent employment";
    }
  }

  return errors;
};

export const hasValidationErrors = (errors) => {
  return Object.keys(errors).length > 0;
};

export const getPersonalInfoValidationSummary = (errors) => {
  const sections = [];

  // Basic Information Section
  const basicInfoErrors = [
    'firstName', 'lastName', 'fatherName', 'motherName', 'alternativePhone',
    'gender', 'dob', 'nationality', 'religion', 'bloodGroup', 'maritalStatus', 'spouseName'
  ].filter(field => errors[field]);

  if (basicInfoErrors.length > 0) {
    sections.push("Basic Information");
  }

  // Emergency Contact Section
  const emergencyErrors = [
    'emergencyRelation', 'emergencyName', 'emergencyPhone'
  ].filter(field => errors[field]);

  if (emergencyErrors.length > 0) {
    sections.push("Emergency Contact Details");
  }

  // Present Address Section
  const presentAddrErrors = [
    'accommodationType', 'present_houseNo',
    'present_street', 'present_landmark', 'present_city',
    'present_state', 'present_pincode'
  ].filter(field => errors[field]);

  if (presentAddrErrors.length > 0) {
    sections.push("Present Address");
  }

  // Permanent Address Section
  const permanentAddrErrors = [
    'permanent_houseNo', 'permanent_street',
    'permanent_landmark', 'permanent_city', 'permanent_state', 'permanent_pincode'
  ].filter(field => errors[field]);

  if (permanentAddrErrors.length > 0) {
    sections.push("Permanent Address");
  }

  // Identification Documents Section
  const documentErrors = [
    'aadharNo', 'aadharFrontDocument', 'aadharBackDocument', 'panNo', 'panDocument'
  ].filter(field => errors[field]);

  if (documentErrors.length > 0) {
    sections.push("Identification Documents");
  }

  // Nominee Details Section
  const nomineeErrors = Object.keys(errors).filter(key => key === 'nominees' || key.startsWith('nominee'));
  if (nomineeErrors.length > 0) {
    sections.push("Nominee Details");
  }

  // Dependent Details Section
  const dependentErrors = Object.keys(errors).filter(key => key === 'dependents' || key.startsWith('dependent'));
  if (dependentErrors.length > 0) {
    sections.push("Dependent Details");
  }

  if (sections.length === 0) return null;

  if (sections.length === 1) {
    return `Please complete the ${sections[0]} section. Fields highlighted in red are required.`;
  }

  const lastSection = sections[sections.length - 1];
  const otherSections = sections.slice(0, -1).join(', ');

  return `Please complete the following sections: ${otherSections}, and ${lastSection}. Fields highlighted in red are required.`;
};
